import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/content";
import { scrollToTarget } from "@/components/site/Reveal";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    scrollToTarget(target);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          : "bg-white/70 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#hero")}
          className="flex items-center gap-2.5 group"
        >
          <span className="w-9 h-9 rounded-lg bg-brick text-white flex items-center justify-center font-heading font-extrabold text-lg">B</span>
          <span className="font-heading font-extrabold text-lg tracking-tight text-charcoal">
            BBS <span className="text-brick">Masonry</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-link-${l.target.replace("#", "")}`}
              onClick={() => go(l.target)}
              className="text-sm font-semibold text-charcoal/70 hover:text-brick transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="nav-call-link"
            href={COMPANY.phoneHref}
            className="flex items-center gap-2 text-sm font-bold text-charcoal hover:text-brick transition-colors"
          >
            <Phone className="w-4 h-4" /> {COMPANY.phoneDisplay}
          </a>
          <button
            data-testid="nav-estimate-btn"
            onClick={() => go("#estimate")}
            className="rounded-full bg-brick hover:bg-brick-dark text-white text-sm font-bold px-5 py-2.5 transition-[background-color,transform] duration-200 hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-brick focus:ring-offset-2"
          >
            Free Estimate
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-10 h-10 flex items-center justify-center text-charcoal"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white border-b border-black/5"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.target}
                  onClick={() => go(l.target)}
                  className="text-left py-3 font-semibold text-charcoal/80 border-b border-black/5"
                >
                  {l.label}
                </button>
              ))}
              <a href={COMPANY.phoneHref} className="flex items-center gap-2 py-3 font-bold text-brick">
                <Phone className="w-4 h-4" /> {COMPANY.phoneDisplay}
              </a>
              <button
                onClick={() => go("#estimate")}
                className="mt-2 rounded-full bg-brick text-white font-bold px-5 py-3"
              >
                Request a Free Estimate
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
